import React, { useEffect, useState, useCallback } from 'react';
import { Layers } from 'lucide-react';
import { type StackCompound } from '../../data/peptidePresets';
import { type PatientMetrics, generateAssessment } from '../../utils/peptideCalculations';

interface StackAssessmentProps {
  patient: PatientMetrics;
  stack: StackCompound[];
}

export function StackAssessment({ patient, stack }: StackAssessmentProps) {
  const [lines, setLines] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);

  const runAssessment = useCallback(() => {
    setLoading(true);
    // Short delay for the "analyzing" animation effect
    setTimeout(() => {
      setLines(generateAssessment(patient, stack));
      setLoading(false);
    }, 500);
  }, [patient, stack]);

  useEffect(() => {
    if (stack.length > 0) {
      runAssessment();
    } else {
      setLines([]);
    }
  }, [patient.sex, patient.age, patient.weightKg, patient.heightCm, stack.length, runAssessment]);

  if (!stack.length) return null;

  return (
    <div className="bg-gradient-to-br from-blue-500/[0.03] to-violet-500/[0.03] border border-violet-500/10 rounded-xl shadow-sm p-5 mt-4">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-7 h-7 rounded-lg flex items-center justify-center bg-gradient-to-br from-blue-500 to-violet-500 text-white text-sm">
          ✦
        </div>
        <div>
          <h2 className="text-sm font-bold">Stack Assessment</h2>
          <p className="text-[10px] text-neutral-400">Analysis based on patient profile + selected compounds</p>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center gap-2.5 py-5 text-neutral-400 text-sm">
          <div className="flex gap-1">
            <span className="w-1.5 h-1.5 rounded-full bg-violet-400 animate-pulse" style={{ animationDelay: '0ms' }} />
            <span className="w-1.5 h-1.5 rounded-full bg-violet-400 animate-pulse" style={{ animationDelay: '200ms' }} />
            <span className="w-1.5 h-1.5 rounded-full bg-violet-400 animate-pulse" style={{ animationDelay: '400ms' }} />
          </div>
          Analyzing stack against patient profile…
        </div>
      ) : (
        <div className="text-[13px] leading-7 text-neutral-700 dark:text-neutral-200">
          {lines.map((line, i) => {
            if (line === '') return <br key={i} />;
            // Parse the assessment lines - they contain <strong> tags from our own generator
            // Strip HTML and render as React elements for safety
            const parts = line.split(/(<strong[^>]*>.*?<\/strong>)/g);
            return (
              <span key={i}>
                {parts.map((part, j) => {
                  const strongMatch = part.match(/<strong(?:\s+style="([^"]*)")?>(.*?)<\/strong>/);
                  if (strongMatch) {
                    const style = strongMatch[1];
                    const text = strongMatch[2];
                    const styleObj: React.CSSProperties = {};
                    if (style?.includes('var(--error)')) styleObj.color = '#DC2626';
                    if (style?.includes('var(--warning)')) styleObj.color = '#D97706';
                    return <strong key={j} style={Object.keys(styleObj).length ? styleObj : undefined} className="text-blue-600 dark:text-blue-400 font-semibold">{text}</strong>;
                  }
                  return <React.Fragment key={j}>{part}</React.Fragment>;
                })}
              </span>
            );
          })}
        </div>
      )}

      <div className="mt-3">
        <button
          onClick={runAssessment}
          className="inline-flex items-center gap-1.5 text-xs font-semibold px-4 py-2 rounded-lg bg-gradient-to-r from-blue-500/10 to-violet-500/10 text-violet-600 dark:text-violet-400 border border-violet-500/20 hover:from-blue-500/20 hover:to-violet-500/20 transition-all"
        >
          <Layers className="w-3.5 h-3.5" />
          Re-analyze Stack
        </button>
      </div>
    </div>
  );
}
